import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import { Actions as PUBLIC_AGREEMENT } from './publicAgreementActions';
import {
  buildContractingEntityQueryParams,
  buildPublicContractingEntityQueryParams,
} from '../../components/Modals/agreementsUtils';
const { apiURL } = require('./apiURL');

const Types = {
  CATALOGUE: 'LEGAL_CATALOGUE',
  STATUS: 'LEGAL_STATUS',
  MARKDOWN: 'LEGAL_MARKDOWN',
  ACCEPT: 'LEGAL_ACCEPT',
  LOADING: 'LEGAL_LOADING',
  ERROR: 'LEGAL_ERROR',
  PENDING_REGISTRATION: 'LEGAL_PENDING_REGISTRATION',
  CLEAR: 'LEGAL_CLEAR',
};

const setLoading = (key, value) => ({
  type: Types.LOADING,
  key,
  value,
});

const setError = (error) => ({
  type: Types.ERROR,
  error,
});

const updateCatalogue = (payload) => ({
  type: Types.CATALOGUE,
  payload,
});

const updateStatus = (payload) => ({
  type: Types.STATUS,
  payload,
});

const updateMarkdown = (documentKind, versionLabel, markdown) => ({
  type: Types.MARKDOWN,
  documentKind,
  versionLabel,
  markdown,
});

const getLegalCatalogue = () => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return;

    dispatch(setLoading('catalogue', true));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/legal/catalogue', null, {
          Organisation: String(state.currentOrganisation),
        })
      );
      dispatch(updateCatalogue(response.data));
    } catch (e) {
      console.log('getLegalCatalogue Error:', e);
      dispatch(setError(e.message || 'Failed to fetch legal catalogue'));
    } finally {
      dispatch(setLoading('catalogue', false));
    }
  };
};

const getLegalStatus = () => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return;

    dispatch(setLoading('status', true));
    try {
      const response = await axios(
        await createSignedRequest('GET', apiURL + '/my/legal/status', null, {
          Organisation: String(state.currentOrganisation),
        })
      );
      dispatch(updateStatus(response.data));
    } catch (e) {
      console.log('getLegalStatus Error:', e);
      dispatch(setError(e.message || 'Failed to fetch legal status'));
    } finally {
      dispatch(setLoading('status', false));
    }
  };
};

const fetchLegalMarkdown = (documentKind, versionLabel) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return;

    const cacheKey = `${documentKind}@${versionLabel || 'latest'}`;
    if (state.legal?.markdownCache?.[cacheKey]) return;

    dispatch(setLoading(cacheKey, true));
    try {
      const path = versionLabel
        ? `/my/legal/markdown/${encodeURIComponent(documentKind)}/${encodeURIComponent(versionLabel)}`
        : `/my/legal/markdown/${encodeURIComponent(documentKind)}`;

      const signed = await createSignedRequest('GET', apiURL + path, null, {
        Organisation: String(state.currentOrganisation),
        Accept: 'text/markdown',
      });
      signed.responseType = 'text';

      const response = await axios(signed);

      dispatch(updateMarkdown(documentKind, versionLabel || 'latest', response.data));
    } catch (e) {
      console.log('fetchLegalMarkdown Error:', e);
      dispatch(setError(e.message || `Failed to fetch markdown for ${documentKind}`));
    } finally {
      dispatch(setLoading(cacheKey, false));
    }
  };
};

const acceptLegalDocuments = (documents) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return null;

    dispatch(setLoading('accept', true));
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/legal/accept',
          JSON.stringify({ documents, method: 'clickwrap' }),
          { Organisation: String(state.currentOrganisation) }
        )
      );

      await dispatch(getLegalStatus());
      return response.data;
    } catch (e) {
      console.log('acceptLegalDocuments Error:', e);
      dispatch(setError(e.message || 'Failed to accept legal documents'));
      return null;
    } finally {
      dispatch(setLoading('accept', false));
    }
  };
};

const requestLegalSignatory = (signatoryEmail, documentKinds, message, firstName, lastName) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) return null;

    dispatch(setLoading('signatory', true));
    try {
      const response = await axios(
        await createSignedRequest(
          'POST',
          apiURL + '/my/legal/acceptance-requests',
          JSON.stringify({ signatoryEmail, documentKinds, message, firstName, lastName }),
          { Organisation: String(state.currentOrganisation) }
        )
      );
      return response.data;
    } catch (e) {
      console.log('requestLegalSignatory Error:', e);
      dispatch(setError(e.message || 'Failed to request legal signatory'));
      return null;
    } finally {
      dispatch(setLoading('signatory', false));
    }
  };
};

const setPendingRegistrationAcceptance = (accepted) => ({
  type: Types.PENDING_REGISTRATION,
  payload: accepted ? { accepted: true, timestamp: Date.now() } : null,
});

const submitDeferredAcceptance = () => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) {
      throw new Error('No organisation context for deferred acceptance');
    }

    const catResponse = await axios(
      await createSignedRequest('GET', apiURL + '/my/legal/catalogue', null, {
        Organisation: String(state.currentOrganisation),
      })
    );
    dispatch(updateCatalogue(catResponse.data));

    const statusResponse = await axios(
      await createSignedRequest('GET', apiURL + '/my/legal/status', null, {
        Organisation: String(state.currentOrganisation),
      })
    );
    dispatch(updateStatus(statusResponse.data));

    const outstanding = statusResponse.data?.outstanding || [];
    if (outstanding.length === 0) return;

    const catalogueItems = catResponse.data?.items || [];
    const documents = outstanding.map((doc) => {
      const item = catalogueItems.find((i) => i.documentKind === doc.documentKind);
      return {
        documentKind: doc.documentKind,
        versionLabel: doc.requiredVersion || item?.versionLabel,
        artifactSha256: item?.sha256 || undefined,
      };
    });

    await axios(
      await createSignedRequest(
        'POST',
        apiURL + '/my/legal/accept',
        JSON.stringify({ documents, method: 'clickwrap' }),
        { Organisation: String(state.currentOrganisation) }
      )
    );

    dispatch(setPendingRegistrationAcceptance(false));
    await dispatch(getLegalStatus());
  };
};

const clearLegalState = () => ({
  type: Types.CLEAR,
});

/**
 * Resolve CYF contracting entity from jurisdiction schedule (preview before order form).
 * Requires Cognito + IAM-signed request to GET /my/legal/contracting-entity.
 * For flows without login/org, use publicAgreementActions.getPublicContractingEntity instead.
 * @param {{ billingCountryCode?: string, billingCountryId?: string, incorporationCountryCode?: string, incorporationCountryId?: string }} params
 */
const getContractingEntity = (params = {}) => {
  return async (dispatch, getState) => {
    const state = getState();
    if (!state.currentOrganisation) {
      return { error: 'not_authorised', status: 401 };
    }

    const query = new URLSearchParams();
    if (params.billingCountryCode) query.set('billingCountryCode', params.billingCountryCode);
    if (params.billingCountryId) query.set('billingCountryId', String(params.billingCountryId));
    if (params.incorporationCountryCode) {
      query.set('incorporationCountryCode', params.incorporationCountryCode);
    }
    if (params.incorporationCountryId) {
      query.set('incorporationCountryId', String(params.incorporationCountryId));
    }

    const qs = query.toString();
    const url = apiURL + '/my/legal/contracting-entity' + (qs ? `?${qs}` : '');

    try {
      const response = await axios(
        await createSignedRequest('GET', url, null, {
          Organisation: String(state.currentOrganisation),
        })
      );
      return response.data;
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      console.log('getContractingEntity Error:', e);
      if (status === 401) {
        return { error: 'not_authorised', status: 401 };
      }
      return {
        error: data?.error || (status === 404 ? 'not_found' : 'request_failed'),
        status,
      };
    }
  };
};

/**
 * Live preview of CYF contracting entity — routes to /my or /public based on session.
 * Use when the UI may run logged-in (admin, consultant) or pre-org (registration).
 * @param {{ incorporationCountryId?: string, billingCountryId?: string, countries?: object[], whiteLabelId?: string, forcePublic?: boolean }} input
 */
const resolveContractingEntityPreview = (input = {}) => {
  return async (dispatch, getState) => {
    const { incorporationCountryId, billingCountryId, countries, whiteLabelId, forcePublic } = input;
    const useAuthed = !forcePublic && Boolean(getState().currentOrganisation);

    if (useAuthed) {
      const params = buildContractingEntityQueryParams({
        incorporationCountryId,
        billingCountryId,
        countries,
      });
      return dispatch(getContractingEntity(params));
    }

    const publicParams = buildPublicContractingEntityQueryParams({
      incorporationCountryId,
      billingCountryId,
      countries,
      whiteLabelId,
    });
    return PUBLIC_AGREEMENT.getPublicContractingEntity(publicParams);
  };
};

const Actions = {
  getLegalCatalogue,
  getLegalStatus,
  fetchLegalMarkdown,
  acceptLegalDocuments,
  requestLegalSignatory,
  setPendingRegistrationAcceptance,
  submitDeferredAcceptance,
  getContractingEntity,
  resolveContractingEntityPreview,
  clearLegalState,
};

export { Actions, Types };
